import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { useAuth } from "../../../app/auth/useAuth";
import { authQueryScope } from "../../../app/query/authQueryScope";
import { useFeedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { merchantPaymentError } from "../../../../../../../shared/frontend/payments/merchantPaymentError";

export interface PersonPaymentProfile {
  paymentMethodName: string | null;
  profile: { paymentMethod: string | null; automaticPaymentsBlocked: boolean; blockReason: string | null; version: number };
  options: { id: string; name: string; paymentMethod: string }[];
  localMethods: { method: number; name: string }[];
}

export function usePersonPaymentProfile(auth: ReturnType<typeof useAuth>, personId: string, text: I18nContextValue["text"]) {
  const query = useQueryClient();
  const feedback = useFeedback();
  const [data, setData] = useState<PersonPaymentProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const key = ["person-payment-profile", ...authQueryScope(auth.api, auth.info), personId];
  const base = `/api/v1/persons/${encodeURIComponent(personId)}/payment-profile`;
  const load = async () => {
    setData(null);
    setLoading(true);
    try {
      const result = await query.fetchQuery({ queryKey: key, staleTime: 0, queryFn: () => auth.api.get<PersonPaymentProfile>(base) });
      setData(result); return result;
    } catch (error) { feedback.error(merchantPaymentError(error, text)); return null; }
    finally { setLoading(false); }
  };
  const perform = async (action: () => Promise<unknown>) => {
    setSaving(true);
    try { await action(); await query.invalidateQueries({ queryKey: key }); await load(); return true; }
    catch (error) { feedback.error(merchantPaymentError(error, text)); return false; }
    finally { setSaving(false); }
  };
  return { data, loading, saving, load,
    save: (method: number | undefined, blocked: boolean, reason: string) => perform(() => auth.api.post(base,
      { method, blocked, reason, expectedVersion: data?.profile.version ?? 0 })),
    setup: async (id: string, paymentMethod: string, language: string) => {
      setSaving(true);
      try {
        const result = await auth.api.post<{ url: string }>(`${base}/setup`, { requestId: crypto.randomUUID(), interfaceId: id, paymentMethod, language });
        return result.url;
      } catch (error) { feedback.error(merchantPaymentError(error, text)); return null; }
      finally { setSaving(false); }
    },
  };
}
